import type { GalleryImageT } from '../../../type/galleryImageT';
import type { GalleryMediaT } from '../../../type/galleryMediaT';
import type { GalleryVideoT } from '../../../type/galleryVideo';

import { Chunk, Order, pipe } from 'effect';

/**
 * Combine images and videos into a single media array
 */
export const combineMedia = (
  images: GalleryImageT[] | null,
  videos: GalleryVideoT[] | null
): GalleryMediaT[] => {
  return [...(images ?? []), ...(videos ?? [])];
};

/**
 * Check if videos exist in the media collection
 */
export const hasVideos = (videos: GalleryVideoT[] | null): boolean => {
  return videos !== null && videos.length > 0;
};

/**
 * Check if media array is empty
 */
export const isMediaEmpty = (media: GalleryMediaT[]): boolean => {
  return media.length === 0;
};

/**
 * Sort gallery media by position, with images prioritized over videos at the same position
 * Uses Effect Chunk and Order for immutable, functional sorting
 *
 * @param media - Array of gallery media items to sort
 * @returns Sorted array with images prioritized at same positions
 *
 * @example
 * ```typescript
 * const media: GalleryMediaT[] = [
 *   { position: 1, mediaType: 'video', path: 'video.mp4' },
 *   { position: 0, mediaType: 'image', path: 'image.jpg' },
 *   { position: 0, mediaType: 'video', path: 'video2.mp4' },
 * ];
 *
 * const sorted = sortMediaByPosition(media);
 * // Result: image at 0, video at 0, video at 1
 * ```
 */
export const sortMediaByPosition = (media: GalleryMediaT[]): GalleryMediaT[] => {
  // Create order for media type (images first, videos second)
  const mediaTypeOrder = Order.make<GalleryMediaT>((a, b) => {
    if (a.mediaType === 'image' && b.mediaType === 'video') return -1;
    if (a.mediaType === 'video' && b.mediaType === 'image') return 1;
    return 0;
  });

  // Create order for position (ascending)
  const positionOrder = Order.mapInput(Order.Number, (item: GalleryMediaT) => item.position);

  // Combine orders: position first, then media type
  const combinedOrder = Order.combine(positionOrder, mediaTypeOrder);

  // Sort using Effect Chunk for immutability
  return pipe(media, Chunk.fromIterable, Chunk.sort(combinedOrder), Chunk.toArray);
};

/**
 * Find the index of the first video in sorted media array
 * @returns Index of first video, or -1 if no videos exist
 */
export const findFirstVideoIndex = (media: GalleryMediaT[]): number => {
  return media.findIndex((item) => item.mediaType === 'video');
};

/**
 * Calculate number of thumbnails to show based on whether videos exist
 */
export const calculateShownThumbs = (videosExist: boolean): number => {
  return videosExist ? 3 : 4;
};
